import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { STORAGE_KEYS } from '../config/constants';

interface ThemeContextType {
  darkMode: boolean;
  privacyMode: boolean;
  toggleDarkMode: () => void;
  togglePrivacyMode: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [darkMode, setDarkMode] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.DARK_MODE);
    if (stored !== null) return stored === 'true';
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return true;
  });
  const [privacyMode, setPrivacyMode] = useState(false);

  // Listen for system appearance changes (only when user hasn't manually overridden)
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      const stored = localStorage.getItem(STORAGE_KEYS.DARK_MODE);
      if (stored === null) {
        setDarkMode(e.matches);
      }
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Keep body background in sync to prevent white bleed on mobile bounce scroll
  useEffect(() => {
    document.body.style.backgroundColor = darkMode ? '#111827' : '#eae4f7';
    // Reveal #root after hydration (hidden via index.html to prevent light-mode flash)
    document.getElementById('root')?.classList.add('hydrated');
  }, [darkMode]);

  const toggleDarkMode = useCallback(() => {
    setDarkMode(prev => {
      const next = !prev;
      localStorage.setItem(STORAGE_KEYS.DARK_MODE, String(next));
      return next;
    });
  }, []);

  const togglePrivacyMode = useCallback(() => {
    setPrivacyMode(prev => !prev);
  }, []);

  return (
    <ThemeContext.Provider
      value={{
        darkMode,
        privacyMode,
        toggleDarkMode,
        togglePrivacyMode,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Theme utility classes
export const getBackgroundClass = (darkMode: boolean): string => {
  return darkMode
    ? 'bg-slate-950'
    : 'bg-[#f8f9ff]';
};

export const getCardClass = (darkMode: boolean): string => {
  return darkMode
    ? 'bg-slate-800/50 border-slate-700/30'
    : 'bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] border-slate-100';
};

export const getTextClass = (darkMode: boolean, variant: 'primary' | 'secondary' = 'primary'): string => {
  if (variant === 'primary') {
    return darkMode ? 'text-white' : 'text-slate-900';
  }
  return darkMode ? 'text-slate-300' : 'text-slate-600';
};
